import Vue, { reactive, set } from 'vue';
import { getUserMeta, getOrganizationMeta, getFormMeta, getFolderMeta, getGlobalStore, getPortalMeta, getUserOrganizationsMeta, getArchivedForms } from '@cognitoforms/api/services/entity-meta-service';
import { archiveForms, unarchiveForms, deleteForm, copyForm, moveForms, type FormCopyMode } from '@cognitoforms/api/services/form-service';
import { createFolder, deleteFolder, restoreFolder, archiveFolder, renameFolder } from '@cognitoforms/api/services/folder-service';
import { getUserPins, pinItem, renameUserPin, unpinItem } from '@cognitoforms/api/services/user-pin-service';
import { addRecentPage, getRecentPages } from '@cognitoforms/api/services/page-history-service';
import { copyEntryView, deleteEntryView, renameEntryView, reorderEntryView } from '@cognitoforms/api/services/entry-view-service';
import { getReferences } from '@cognitoforms/api/services/form-details-service';
import { getAvatarSVG } from '@cognitoforms/utils/avatar';
import { Deferred } from 'src/utilities/deferred';
import { getResources } from 'src/localization/resource-util';
import { PageType } from '@cognitoforms/types/server-types/model/recent-page+-page-type';
import { ViewType } from '@cognitoforms/types/server-types/forms/model/view-type';
import { UserPinType } from '@cognitoforms/types/server-types/model/user-pin-type';
import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
import type VueRouter from 'vue-router';
import type { DeepReadonly } from 'vue';
import type { UserFolderMeta } from '@cognitoforms/types/server-types/core/services/user-folder-meta';
import type { OrganizationMeta } from '@cognitoforms/types/server-types/core/services/organization-meta';
import type { UserEntryViewMeta } from '@cognitoforms/types/server-types/core/services/user-entry-view-meta';
import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';
import type { UserMeta } from '@cognitoforms/types/server-types/core/services/user-meta';
import type { OriginalUserMeta } from '@cognitoforms/types/server-types/core/services/original-user-meta';
import type { UserPin } from '@cognitoforms/types/server-types/model/user-pin';
import type { RecentPage } from '@cognitoforms/types/server-types/model/recent-page';
import type { FormCopyInfo } from '@cognitoforms/types/server-types/forms/model/form-copy-info';
import type { SubmitterTrackingDecision } from '@cognitoforms/types/server-types/forms/services/submitter-tracking-decision';
import type { LookupReferences } from '@cognitoforms/types/server-types/services/forms/lookup-references';
import type { PortalMeta } from '@cognitoforms/types/server-types/core/services/portal-meta';
import type { OrgAppearanceSettingsMeta } from '@cognitoforms/types/server-types/core/services/org-appearance-settings-meta';
import type { LocalizationResources } from 'src/localization/resources/localization-resources.generated';

export interface GlobalState {
	user: UserMeta;
	originalUser: OriginalUserMeta;
	organization: OrganizationMeta;
	folders: Record<string, UserFolderMeta>,
	forms: Record<string, UserFormMeta>,
	allForms: Record<string, UserFormMeta>,
	allFolders: Record<string, UserFolderMeta>,
	recentPages: RecentPage[],
	userPins: UserPin[],
	portal: PortalMeta,
	orgAppearanceSettings: OrgAppearanceSettingsMeta
	cachedCurrentForm: UserFormMeta,
	cachedCurrentView: UserEntryViewMeta,
	cachedCurrentFormReferences: Record<string, LookupReferences>,
	currentFormId: string,
	currentViewId: string,
	exceededAllowedEntries: boolean,
	isInViewAsUserMode: boolean,
	isRestrictedSupportUser: boolean,
	isInReadOnlyMode: boolean,
	hasArchivedForms: boolean,
	hasLoadedArchivedForms: boolean,
	ready: Deferred<void>,
	resourcesReady: Deferred<LocalizationResources>
}

const globalState = reactive<GlobalState>({
	user: null,
	originalUser: null,
	organization: null,
	folders: {},
	forms: {},
	allForms: {},
	allFolders: {},
	recentPages: null,
	userPins: [],
	portal: null,
	orgAppearanceSettings: null,
	cachedCurrentForm: null,
	cachedCurrentView: null,
	cachedCurrentFormReferences: {},
	currentFormId: null,
	currentViewId: null,
	exceededAllowedEntries: false,
	isInViewAsUserMode: false,
	isRestrictedSupportUser: false,
	isInReadOnlyMode: false,
	hasArchivedForms: false,
	hasLoadedArchivedForms: false,
	ready: new Deferred(),
	resourcesReady: new Deferred<LocalizationResources>()
});

const formUpdates = {};
const maxSafeTimeout = 2147483647;
const maxGuestEntries = 1000;

// Used to track the number of entries accessible by a guest user
let accessibleEntries = 0;

export const globalStore = reactive({
	async initialize() {
		const result = await getGlobalStore();
		if (!result)
			return;

		globalState.user = result.UserMeta;
		globalState.originalUser = result.OriginalUserMeta;
		globalState.organization = result.OrganizationMeta;
		globalState.userPins = result.UserPins;
		globalState.portal = result.PortalMeta;
		globalState.orgAppearanceSettings = result.PortalMeta?.OrgAppearanceSettings;
		globalState.hasArchivedForms = result.HasArchivedForms;

		globalState.exceededAllowedEntries = false;
		accessibleEntries = 0;

		globalState.forms = Object.fromEntries(
			result.UserFormMeta.map(form => {
				form.EntryViews = this.sortBySequenceKey(form.EntryViews);
				this.computeTasksForForm(form);
				accessibleEntries += form.Entries;
				this.scheduleFormRefresh(form);
				return [form.Id, form];
			})
		);

		if (globalState.organization.Role === ClientRole.Guest)
			globalState.exceededAllowedEntries = accessibleEntries > maxGuestEntries;

		globalState.allForms = Object.fromEntries(
			result.AdminFormMeta.map(form => [form.Id, form])
		);

		globalState.folders = Object.fromEntries(
			result.UserFolderMeta.map(folder => [folder.Id, folder])
		);

		globalState.allFolders = Object.fromEntries(
			result.AdminFolderMeta.map(folder => [folder.Id, folder])
		);

		const resources = await getResources(globalState.organization.LanguageCode);
		globalState.resourcesReady.resolve(resources);

		globalState.ready.resolve();

		// Get user's organizations separately to reduce initial load
		const organizations = await getUserOrganizationsMeta();
		if (!organizations)
			return;

		globalState.user.Organizations = organizations;
	},

	computeTasksForForm(form: UserFormMeta) {
		if (!form?.EntryViews.length)
			return;

		const tasks = form.EntryViews
			.filter(v => v.IsTaskView)
			.reduce((sum, v) => sum + (v.Entries ?? 0), 0);

		set(form, 'Tasks', tasks);
		set(globalState.forms, form.Id, form);

		if (globalState.currentFormId === form.Id)
			this.updateCurrentFormCache(form);
	},

	get isReady(): Promise<void> {
		return globalState.ready.promise;
	},

	get resourcesReady(): Promise<LocalizationResources> {
		return globalState.resourcesReady.promise;
	},

	get fullName(): Readonly<string> {
		if (!globalState.user)
			return;

		return globalState.user.LastName
			? `${globalStore.user.FirstName} ${globalStore.user.LastName}`
			: globalStore.user.FirstName;
	},

	get user(): Readonly<UserMeta> {
		return Object.assign({}, globalState.user);
	},

	get originalUser(): Readonly<OriginalUserMeta> {
		return Object.assign({}, globalState.originalUser);
	},

	get avatar(): Readonly<string> {
		if (!globalState.user)
			return;

		return (globalState.user.AvatarUrl?.trim() === '' && (globalState.user.FirstName ?? globalState.user.Email))
			? getAvatarSVG(globalState.user)
			: globalState.user.AvatarUrl;
	},

	get organization(): DeepReadonly<OrganizationMeta> {
		return Object.assign({}, globalState.organization);
	},

	get forms(): DeepReadonly<Record<string, UserFormMeta>> {
		if (!globalState.exceededAllowedEntries)
			return Object.assign({}, globalState.forms);
		else {
			// Sort forms from most to least number of entries
			const sortedEntries = Object.entries(globalState.forms).toSorted(([, formA], [, formB]) => formB.Entries - formA.Entries);

			let totalEntries = 0;
			return Object.fromEntries(sortedEntries.filter(([, form]) => {
				if (totalEntries >= maxGuestEntries)
					return false;

				totalEntries += form.Entries;
				return true;
			}));
		}
	},

	get allForms(): DeepReadonly<Record<string, UserFormMeta>> {
		return Object.assign({}, globalState.forms, globalState.allForms);
	},

	get exceededAllowedEntries(): Readonly<boolean> {
		return globalState.exceededAllowedEntries;
	},

	get folders(): DeepReadonly<Record<string, UserFolderMeta>> {
		return Object.assign({}, globalState.folders);
	},

	get allFolders(): DeepReadonly<Record<string, UserFolderMeta>> {
		return Object.assign({}, globalState.folders, globalState.allFolders);
	},

	get recentPages(): ReadonlyArray<RecentPage> {
		if (!globalState.recentPages)
			return null;
		return [...globalState.recentPages];
	},

	get totalTaskCount(): Readonly<number> {
		return Object.values(globalState.forms).filter(f => !f.IsArchived).reduce((sum, f) => sum + f.Tasks, 0);
	},

	get userPins(): Readonly<UserPin[]> {
		return Object.assign([], globalState.userPins);
	},

	get portal(): Readonly<PortalMeta> {
		return Object.assign({}, globalState.portal);
	},

	get orgAppearanceSettings(): Readonly<OrgAppearanceSettingsMeta> {
		return Object.assign({}, globalState.orgAppearanceSettings);
	},

	get currentForm(): DeepReadonly<UserFormMeta & { IsDeleted: boolean }> {
		if (!globalState.currentFormId)
			return null;

		const currentForm = globalState.forms[globalState.currentFormId];
		return (currentForm) ? { ...currentForm, IsDeleted: false } : { ...globalState.cachedCurrentForm, IsDeleted: true };
	},

	get currentView(): DeepReadonly<UserEntryViewMeta & { IsDeleted: boolean }> {
		if (!globalState.currentFormId || !globalState.currentViewId)
			return null;

		const currentEntryView = this.currentForm?.EntryViews?.find(x => x.Id === globalState.currentViewId);
		return (currentEntryView) ? { ...currentEntryView, IsDeleted: this.currentForm.IsDeleted } : { ...globalState.cachedCurrentView, IsDeleted: true };
	},

	get currentFormReferences(): LookupReferences {
		if (!globalState.currentFormId)
			return;

		return globalState.cachedCurrentFormReferences[globalState.currentFormId];
	},

	get formsWithTaskViews() : Readonly<UserFormMeta[]> {
		return Object.values(globalStore.forms).filter(form => !form.IsArchived && form.EntryViews.some(entryView => entryView.IsTaskView)) as UserFormMeta[];
	},

	get taskViews(): Readonly<UserEntryViewMeta[]> {
		return Object.values(globalStore.forms).filter(f => !f.IsArchived).flatMap(f => f.EntryViews.filter(v => v.IsTaskView));
	},

	get isInViewAsUserMode(): Readonly<boolean> {
		return globalState.isInViewAsUserMode;
	},

	get isRestrictedSupportUser(): Readonly<boolean> {
		return globalState.isRestrictedSupportUser;
	},

	get isInReadOnlyMode(): Readonly<boolean> {
		return globalState.isInReadOnlyMode;
	},

	get hasArchivedForms(): Readonly<boolean> {
		return globalState.hasArchivedForms;
	},

	setHasArchivedForms() {
		globalState.hasArchivedForms = Object.values(globalState.forms).some(form => form.IsArchived);
	},

	clearCurrentForm() {
		globalState.currentFormId = null;
		this.clearCurrentView();
	},

	clearCurrentView() {
		globalState.currentViewId = null;
	},

	setCurrentView(viewId: string) {
		if (!/\d+-\d+/.test(viewId))
			return console.error('Invalid view id provided!');
		globalState.currentViewId = viewId;

		const formId = viewId.split('-')[0];
		this.setCurrentForm(formId);
	},

	setCurrentForm(formId: string) {
		globalState.currentFormId = formId;
		this.updateCurrentFormCache(globalState.forms[formId]);
	},

	updateOrganizationLocally(meta: Partial<OrganizationMeta>) {
		const mergedMeta = Object.assign(globalState.organization, meta);
		set(globalState, 'organization', mergedMeta);
	},

	updateUserLocally(meta: Partial<UserMeta>) {
		const mergedMeta = Object.assign(globalState.user, meta);
		set(globalState, 'user', mergedMeta);
	},

	updatePortalLocally(meta: Partial<PortalMeta>) {
		const mergedMeta = Object.assign(globalState.portal, meta);
		set(globalState, 'portal', mergedMeta);
	},

	addFormLocally(meta: Partial<UserFormMeta> & { Id: string, InternalName: string, Name: string, Title: string, UrlName: string }) {
		const newMeta: Omit<UserFormMeta, 'FolderId' | 'ValidUntil'> = {
			EntryViews: [],
			Entries: 0,
			Tasks: 0,
			UnreadEntries: 0,
			LockedEntries: 0,
			CanManageForm: true,
			CanManageEntries: true,
			CanReviewEntries: true,
			IsArchived: false,
			IsSingleViewForm: true,
			SecureLookupsForGuests: false,
			LastModifiedOn: null,
			PreservedCaseUrlName: meta.PreservedCaseUrlName ?? meta.UrlName,
			...meta
		};

		set(globalState.forms, meta.Id, newMeta);
	},

	updateFormLocally(formId: string, meta: Partial<UserFormMeta>) {
		const mergedForm = Object.assign(globalState.forms[formId], meta);

		set(globalState.forms, formId, mergedForm);
		this.computeTasksForForm(mergedForm);
		if (globalState.currentFormId === mergedForm.Id)
			this.updateCurrentFormCache(mergedForm);
		// Always update references if already loaded
		else if (globalState.cachedCurrentFormReferences[formId])
			this.updateCurrentFormReferencesCache(mergedForm);

		if (meta.IsArchived)
			window.Cognito?.Forms?.unregisterPersonForm?.(formId);
	},

	addEntryViewLocally(meta: Partial<UserEntryViewMeta> & { Id: string, InternalName: string, Name: string, RoleId: number }) {
		const newMeta: Omit<UserEntryViewMeta, 'Token' | 'SequenceKey' | 'TaskDueDate' | 'TaskPriority' | 'AfterSubmitViewId'> = {
			IsTaskView: false,
			Type: ViewType.Form,
			Entries: 0,
			PreventEntryCreation: false,
			ShowPageBreaks: true,
			IsUserSpecific: false,
			IsAssignedRole: false,
			LastUpdated: new Date(),
			Valid: true,
			...meta
		};

		const formId = meta.Id.split('-')[0];
		const form = globalState.forms[formId];

		const newEntryViews = [ ...form.EntryViews ];
		newEntryViews.push(newMeta as UserEntryViewMeta);
		set(form, 'EntryViews', this.sortBySequenceKey(newEntryViews));

		this.computeTasksForForm(form);
		this.scheduleFormRefresh(form);
	},

	updateEntryViewLocally(viewId: string, meta: Partial<UserEntryViewMeta>) {
		const formId = viewId.split('-')[0];
		const form = globalState.forms[formId];
		const viewIndex = form.EntryViews.findIndex(x => x.Id === viewId);
		if (viewIndex === -1) {
			this.addEntryViewLocally({ ...meta, Id: viewId } as UserEntryViewMeta);
			return;
		}

		const entryViews = [ ...form.EntryViews ];

		// With Independent Task Views enabled, whether or not a user sees entries in a view as tasks is not solely-determined by IsTaskView.
		// The condition (view.IsTaskView && (isAssignedRole || view.SharedWithMe || isLimitedAccess || User.IsGuest)) is left to the
		// server to evaluate; hence, we preserve the previous IsTaskView value.
		meta.IsTaskView = entryViews[viewIndex].IsTaskView;
		Object.assign(entryViews[viewIndex], meta);

		if (globalState.currentViewId === entryViews[viewIndex].Id)
			this.updateCurrentViewCache(entryViews[viewIndex]);

		set(form, 'EntryViews', this.sortBySequenceKey(entryViews));
		this.computeTasksForForm(form);
		this.scheduleFormRefresh(form);
	},

	addFolderLocally(meta: UserFolderMeta) {
		set(globalState.folders, meta.Id, meta);
	},

	updateFolderLocally(folderId: string, meta: Partial<UserFolderMeta>) {
		const mergedFolder = Object.assign(globalState.folders[folderId], meta);
		set(globalState.folders, folderId, mergedFolder);
	},

	async updateRecentPages(recentPage: RecentPage) {
		const res = await addRecentPage(recentPage);
		globalState.recentPages = res.RecentPages;
	},

	trackRecentPage(page: RecentPage, router: VueRouter) {
		// Wait for global store initialization
		if (globalState.ready.isPending)
			return this.isReady.then(() => this.trackRecentPage(page, router));

		// Do not track recent pages for guests or when viewing the app as another user
		if (globalState.organization.Role === ClientRole.Guest || globalState.isInReadOnlyMode)
			return;

		const currentUrl = router.currentRoute;
		const isSameEntryView = () => {
			if (page.Type !== PageType.FormView && page.Type !== PageType.GridView)
				return false;

			const oldParams = currentUrl.params;
			const newParams = router.currentRoute.params;

			return oldParams.formUrlName === newParams.formUrlName && oldParams.entryView === newParams.entryView;
		};

		setTimeout(() => {
			if ((currentUrl === router.currentRoute || isSameEntryView()) && !globalState.isInReadOnlyMode)
				this.updateRecentPages(page);
		}, 6000);
	},

	async loadUser() {
		const user = await getUserMeta();
		globalState.user = user;
	},

	async loadOrganization() {
		const organization = await getOrganizationMeta();
		globalState.organization = organization;
	},

	async loadPins() {
		const pins = await getUserPins();
		globalState.userPins = pins;
	},

	setLastUpdated(viewIds: string[]) {
		const now = new Date();
		const formIdToViewIds: Record<string, string[]> = viewIds.reduce((acc, viewId) => {
			if (!viewId) return acc;

			const formId = viewId.split('-')[0];
			if (!acc[formId]) {
				acc[formId] = [];
			}
			acc[formId].push(viewId);
			return acc;
		}, {});

		Object.entries(formIdToViewIds).forEach(([formId, viewIds]) => {
			const entryViews = globalState.forms[formId]?.EntryViews;
			if (entryViews) {
				viewIds.forEach(viewId => {
					const entryView = entryViews.find(entryView => entryView.Id === viewId);
					if (entryView)
						entryView.LastUpdated = now;
				});
			}
		});
	},

	async internalLoadForms(formIds: string[], Forms: UserFormMeta[], Folders: UserFolderMeta[], AdminForms: UserFormMeta[], AdminFolders: UserFolderMeta[]) {
		const requestedForms = new Set(formIds ?? []);
		const checkFolders = new Set<string>();

		Folders.forEach(f => set(globalState.folders, f.Id, f));
		AdminFolders.forEach(f => set(globalState.allFolders, f.Id, f));
		Forms.forEach(form => {
			// Remove form from allForms if it is now fully visible to the user
			if (globalState.allForms[form.Id])
				Vue.delete(globalState.allForms, form.Id);

			form.EntryViews = this.sortBySequenceKey(form.EntryViews);

			// Read the old entry count before computeTasksForForm updates globalState.forms
			const currentFormEntryCount = globalState.forms[form.Id]?.Entries ?? 0;
			accessibleEntries += form.Entries - currentFormEntryCount;

			this.computeTasksForForm(form);

			requestedForms.delete(form.Id);

			// Get the form's folder if not already loaded
			if (form.FolderId && !globalState.folders[form.FolderId])
				checkFolders.add(form.FolderId);

			this.scheduleFormRefresh(form);
		});

		// Delete forms that were requested and not returned by the endpoint
		requestedForms.forEach(formId => {
			const folderId = globalState.forms[formId]?.FolderId;
			if (folderId)
				checkFolders.add(folderId);

			accessibleEntries -= globalState.forms[formId]?.Entries ?? 0;

			Vue.delete(globalState.forms, formId);
		});

		AdminForms.forEach(form => {
			set(globalState.allForms, form.Id, form);
			requestedForms.delete(form.Id);
		});

		// Delete forms from allForms that were requested and not returned
		requestedForms.forEach(formId => {
			Vue.delete(globalState.allForms, formId);
		});

		// Check if the maximum number of entries has been exceeded for guests
		globalState.exceededAllowedEntries = accessibleEntries > maxGuestEntries && globalState.organization.Role === ClientRole.Guest;

		// Check if user still has access to folders when forms are deleted
		if (checkFolders.size > 0)
			await this.loadFolders(Array.from(checkFolders));

		// Update forms in global state after all folders have been loaded
		Forms.forEach(form => {
			set(globalState.forms, form.Id, form);
			if (globalState.currentFormId === form.Id)
				this.updateCurrentFormCache(form);
			else if (globalState.cachedCurrentFormReferences[form.Id])
				this.updateCurrentFormReferencesCache(form);
		});
	},

	async loadForms(formIds: string[]) {
		const { Forms, Folders, AdminForms, AdminFolders } = await getFormMeta(formIds);
		await this.internalLoadForms(formIds, Forms, Folders, AdminForms, AdminFolders);
	},

	async loadArchivedFormsIfNeeded() {
		if (!globalState.hasLoadedArchivedForms) {
			const { Forms, Folders, AdminForms, AdminFolders } = await getArchivedForms();
			await this.internalLoadForms([], Forms, Folders, AdminForms, AdminFolders);
			globalState.hasLoadedArchivedForms = true;
			globalState.hasArchivedForms = Forms.length > 0;
		}
	},

	async updateCurrentFormCache(form: UserFormMeta) {
		if (form) {
			set(globalState, 'cachedCurrentForm', { ...globalState.forms[form.Id] });
			const view = globalState.forms[form.Id].EntryViews.find(e => e.Id === globalState.currentViewId);
			this.updateCurrentViewCache(view);
			this.updateCurrentFormReferencesCache(form);
		}
	},

	async updateCurrentViewCache(view: UserEntryViewMeta) {
		if (view)
			set(globalState, 'cachedCurrentView', { ...view });
	},

	async updateCurrentFormReferencesCache(form) {
		const references = await getReferences(form.Id);
		set(globalState.cachedCurrentFormReferences, form.Id, references);
	},

	scheduleFormRefresh(form: UserFormMeta) {
		const timestamp = new Date(form.ValidUntil).getTime();

		// Ensure date is valid and abort if it's not
		if (!form.ValidUntil || Number.isNaN(timestamp) || new Date(form.ValidUntil).getFullYear() <= 1970)
			return;

		clearTimeout(formUpdates[form.Id]);

		let timeout = Math.min(timestamp - Date.now(), maxSafeTimeout);
		if (timeout < 0)
			timeout = 10000;

		formUpdates[form.Id] = setTimeout(() => {
			this.loadForms([form.Id]);
		}, timeout);
	},

	clearFormUpdates() {
		for (const key in formUpdates)
			clearTimeout(formUpdates[key]);
	},

	async loadFolders(folderIds: string[]) {
		const { Folders, AdminFolders } = await getFolderMeta(folderIds);

		// Create a set of requested entities to track which entities were not returned
		const requestedEntities = new Set(folderIds ?? []);

		Folders.forEach(f => {
			set(globalState.folders, f.Id, f);

			// If an admin folder is now visible as a user folder, remove from allFolders
			if (globalState.allFolders[f.Id])
				Vue.delete(globalState.allFolders, f.Id);

			requestedEntities.delete(f.Id);
		});

		// Delete folders that were requested that were in folders and not returned by the endpoint
		requestedEntities.forEach(folderId => {
			Vue.delete(globalState.folders, folderId);
		});

		AdminFolders.forEach(f => {
			set(globalState.allFolders, f.Id, f);
			requestedEntities.delete(f.Id);
		});

		// Delete folders that were requested that were in allFolders and not returned by the endpoint
		requestedEntities.forEach(folderId => {
			Vue.delete(globalState.allFolders, folderId);
		});
	},

	async loadRecentPages() {
		globalState.ready.then(async () => {
			const isGuest = globalState.organization.Role === ClientRole.Guest;
			globalState.recentPages = (!isGuest) ? await getRecentPages() : [];
		});
	},

	async loadPortalMeta() {
		const portalMeta = await getPortalMeta();
		globalState.portal = portalMeta;
		globalState.orgAppearanceSettings = portalMeta.OrgAppearanceSettings;
	},

	sortBySequenceKey(meta: UserEntryViewMeta[]) {
		return meta.sort((a, b) => {
			// Sort form views after all other view types for guests
			if (globalState.organization.Role === ClientRole.Guest) {
				const aIsForm = a.Type === ViewType.Form;
				const bIsForm = b.Type === ViewType.Form;
				if (aIsForm !== bIsForm) return aIsForm ? 1 : -1;
			}

			// If transient view is present, place it first since saved views will be locked
			if (!globalState.organization.Plan.AllowEntryViews) {
				if (a.Id.endsWith('-0'))
					return -1;
				else if (b.Id.endsWith('-0'))
					return 1;
			}

			// Fallback to Name without spaces if SequenceKey is not present
			const keyA = a.SequenceKey || a.Name.replace(/\s/g, '');
			const keyB = b.SequenceKey || b.Name.replace(/\s/g, '');
			return keyA.localeCompare(keyB);
		});
	},

	async pinItem(id: string, name: string = null, type: UserPinType) {
		if (!globalState.userPins.find(p => p.Id === id))
			return pinItem(id, name, type).then(() => {
				let pinId = id;
				if (type === UserPinType.EntryView && id.endsWith('-0'))
					pinId = id.replace('-0', '-1');

				const existingIndex = globalState.userPins.findIndex(p => p.Id === pinId);
				if (existingIndex < 0)
					globalState.userPins.push({ Id: id, Name: name, Type: type });
			});
		else
			return Promise.reject(new Error('Item is already pinned'));
	},

	async unpinItem(userPinId: string) {
		return unpinItem(userPinId).then(() => {
			const index = globalState.userPins.findIndex(i => i.Id === userPinId);
			Vue.delete(globalState.userPins, index);
		});
	},

	// Helper for handling 'viewless' forms when showing tree view of forms and entry views
	getEffectivePin(viewId: string) {
		const formId = viewId.split('-')[0];
		const zeroView = globalState.forms[formId]?.EntryViews?.find(view => view.Id === formId + '-0');
		if (viewId.split('-')[1] === '1' && zeroView)
			return zeroView.Id;
		return viewId;
	},

	async createFolder() {
		const defaultFolderName = 'New Folder';
		const folderId = createFolder(defaultFolderName).then((newFolder) => {
			this.addFolderLocally(newFolder);
			return newFolder.Id;
		});

		return folderId;
	},

	async copyForm(sourceOrg: string, destinationOrg : string, destinationFolderId: string, copyMode: FormCopyMode, formCopyInfo: FormCopyInfo[], submitterTrackingDecision: SubmitterTrackingDecision | null) {
		return copyForm(sourceOrg, destinationOrg, destinationFolderId, copyMode, formCopyInfo, submitterTrackingDecision).then((response) => {
			if (sourceOrg === destinationOrg) {
				const formId = formCopyInfo[0].FormId;

				this.addFormLocally({
					...globalState.forms[formId],
					Id: response.Id,
					InternalName: response.LoweredInternalName,
					Name: response.Name,
					FolderId: destinationFolderId,
					EntryViews: globalState.forms[formId].EntryViews.map(view => ({
						...view,
						Id: `${response.Id}-${view.Id.split('-')[1]}`,
						Entries: 0
					}))
				});
			}

			return response;
		});
	},

	async copyView(viewId: string, newName: string) {
		const formId = viewId.split('-')[0];
		const originalView = globalState.forms[formId].EntryViews.find(v => v.Id === viewId);
		return copyEntryView(formId, viewId, newName).then((view: { Id: string, Name: string, InternalName: string }) => {
			this.addEntryViewLocally({
				...originalView,
				Id: view.Id,
				Name: view.Name,
				InternalName: view.InternalName
			});
		});
	},

	async renameUserPin(pinId: string, newName: string) {
		const userPin = globalState.userPins.find(p => p.Id === pinId);

		if (userPin.Name === newName)
			return Promise.resolve();

		return renameUserPin(pinId, newName).then(() => {
			userPin.Name = newName;
		}).catch((e) => {
			// trigger an update if the rename fails to revert the rename in the UI
			const pinIndex = globalState.userPins.findIndex(p => p.Id === pinId);
			globalState.userPins[pinIndex] = { ...globalState.userPins[pinIndex] }[0];
		});
	},

	async renameView(viewId: string, newName: string) {
		const view = globalState.forms[viewId.split('-')[0]].EntryViews.find(v => v.Id === viewId);

		if (view.Name === newName)
			return Promise.resolve();

		const formId = viewId.split('-')[0];
		return renameEntryView(formId, viewId, newName).then(() => {
			this.updateEntryViewLocally(viewId, { Name: newName });
		}).catch((e) => {
			// trigger an update if the rename fails to revert the rename in the UI
			const viewIndex = globalState.forms[formId].EntryViews.findIndex(v => v.Id === viewId);
			globalState.forms[formId].EntryViews[viewIndex] = { ...globalState.forms[formId].EntryViews[viewIndex] }[0] ?? view;
		});
	},

	async renameFolder(folderId: string, newName: string) {
		const folder = globalState.folders[folderId];

		if (folder.Name === newName)
			return Promise.resolve();

		return renameFolder(folderId, newName).then(() => {
			this.updateFolderLocally(folderId, { Name: newName });
		}).catch((e) => {
			// trigger an update if the rename fails to revert the rename in the UI
			globalState.folders[folderId] = { ...globalState.folders[folderId] }[0] ?? folder;
		});
	},

	async archiveForms(formIds: string[]) {
		return archiveForms(formIds).then(() => {
			formIds.forEach(formId => this.updateFormLocally(formId, { IsArchived: true }));
			this.setHasArchivedForms();
		});
	},

	async restoreForms(formIds: string[]) {
		return unarchiveForms(formIds).then(() => {
			formIds.forEach(formId => {
				this.updateFormLocally(formId, { IsArchived: false });

				// Ensure that folder is not archived
				const folderId = globalState.forms[formId].FolderId;
				if (globalState.folders[folderId])
					this.updateFolderLocally(folderId, { IsArchived: false });
			});
			// Reset hasArchivedForms if no forms are archived
			this.setHasArchivedForms();
		});
	},

	async archiveFolder(folderId: string) {
		return archiveFolder(folderId).then(() => {
			const formsInFolder = Object.values(globalState.forms).filter(form => form.FolderId === folderId);
			formsInFolder.forEach(form => this.updateFormLocally(form.Id, { IsArchived: true }));
			this.updateFolderLocally(folderId, { IsArchived: true });
			this.setHasArchivedForms();
		});
	},

	async restoreFolder(folderId: string) {
		return restoreFolder(folderId).then(() => {
			const formsInFolder = Object.values(globalState.forms).filter(form => form.FolderId === folderId);
			formsInFolder.forEach(form => this.updateFormLocally(form.Id, { IsArchived: false }));
			this.updateFolderLocally(folderId, { IsArchived: false });
			this.setHasArchivedForms();
		});
	},

	async moveForms(formIds: string[], folderId: string = null) {
		return moveForms(formIds, folderId).then(() => {
			formIds.forEach(formId => {
				this.updateFormLocally(formId, { FolderId: folderId });
			});
		});
	},

	async deleteFolder(folderId: string) {
		return deleteFolder(folderId).then(() => {
			Vue.delete(globalState.folders, folderId);
			if (globalState.allFolders[folderId])
				Vue.delete(globalState.allFolders, folderId);
		});
	},

	async deleteForm(formId: string) {
		return deleteForm(formId).then(() => {
			Vue.delete(globalState.forms, formId);
			if (globalState.allForms[formId])
				Vue.delete(globalState.allForms, formId);
			window.Cognito?.Forms?.unregisterPersonForm?.(formId, true);
			// Reset hasArchivedForms if no forms are archived
			this.setHasArchivedForms();
		});
	},

	async deleteView(viewId: string) {
		const formId = viewId.split('-')[0];
		return deleteEntryView(formId, viewId).then(() => {
			Vue.delete(globalState.forms[formId].EntryViews, globalState.forms[formId].EntryViews.findIndex(v => v.Id === viewId));
			this.computeTasksForForm(globalState.forms[formId]);
		});
	},

	async reorderEntryView(viewId: string, afterViewId: string) {
		const formId = viewId.split('-')[0];
		return reorderEntryView(formId, viewId.split('-')[1], afterViewId).then((sequenceKey) => {
			this.updateEntryViewLocally(viewId, { SequenceKey: sequenceKey });
		});
	},

	setIsInViewAsUserMode(enabled: boolean) {
		globalState.isInViewAsUserMode = enabled;
		globalState.isInReadOnlyMode = enabled || globalState.isRestrictedSupportUser;
	},

	setIsRestrictedSupportUser(enabled: boolean) {
		globalState.isRestrictedSupportUser = enabled;
		globalState.isInReadOnlyMode = enabled || globalState.isInViewAsUserMode;
	}
});

export type GlobalStore = typeof globalStore;
